/**
 * Booking mutations hooks
 * Handles create and update operations for bookings
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Booking, BookingFormData } from "@/types/admin/booking";

/**
 * Helper function to find or create a guest
 */
async function findOrCreateGuest(bookingData: BookingFormData): Promise<string> {
  if (bookingData.guest_id) {
    return bookingData.guest_id;
  }

  const { data: existingGuests } = await supabase
    .from("guests")
    .select("id")
    .eq("name", bookingData.guest_name)
    .limit(1);

  if (existingGuests && existingGuests.length > 0) {
    return existingGuests[0].id;
  }

  const { data: newGuest, error: guestError } = await supabase
    .from("guests")
    .insert({
      name: bookingData.guest_name,
      email: bookingData.guest_email || null,
      phone: bookingData.guest_phone || null,
      origin_id: bookingData.origin_id,
    })
    .select("id")
    .single();

  if (guestError) {
    console.error("Error creating guest:", guestError);
    throw guestError;
  }

  return newGuest.id;
}

/**
 * Booking select query for mutations
 */
const BOOKING_SELECT_QUERY = `
  id,
  booking_id,
  property_id,
  property_short_name,
  guest_id,
  guest_name,
  guest_email,
  guest_phone,
  origin_id,
  check_in,
  check_out,
  nights,
  total_guests,
  children,
  total_payment,
  cleaning_fee,
  base_payment,
  pets_fee,
  early_checkin_fee,
  late_checkout_fee,
  other_fees,
  estimated_payout,
  stay_completed,
  payment_status,
  status,
  note,
  created_at,
  updated_at,
  origins!inner (
    name
  )
`;

/**
 * Invalidate all booking-related queries
 */
function invalidateBookingQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ["bookings"] });
  queryClient.invalidateQueries({ queryKey: ["availability"] });
  queryClient.invalidateQueries({ queryKey: ["calendar-data"] });
}

/**
 * Hook to create a new booking
 */
export const useCreateBooking = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (bookingData: BookingFormData): Promise<Booking> => {
      // Validate origin_id
      if (!bookingData.origin_id || bookingData.origin_id.trim() === '') {
        throw new Error('El origen es requerido');
      }

      const guestId = await findOrCreateGuest(bookingData);

      const checkIn = new Date(bookingData.check_in);
      const checkOut = new Date(bookingData.check_out);
      const nights = Math.ceil(
        (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24),
      );

      const { data: booking, error } = await supabase
        .from("bookings")
        .insert({
          property_id: bookingData.property_id,
          origin_id: bookingData.origin_id,
          check_in: bookingData.check_in,
          check_out: bookingData.check_out,
          guest_id: guestId,
          guest_name: bookingData.guest_name,
          guest_email: bookingData.guest_email || null,
          guest_phone: bookingData.guest_phone || null,
          total_guests: bookingData.total_guests,
          children: bookingData.children || 0,
          total_payment: bookingData.total_payment,
          cleaning_fee: bookingData.cleaning_fee || 0,
          base_payment: bookingData.base_payment || 0,
          pets_fee: bookingData.pets_fee || 0,
          early_checkin_fee: bookingData.early_checkin_fee || 0,
          late_checkout_fee: bookingData.late_checkout_fee || 0,
          other_fees: bookingData.other_fees || 0,
          estimated_payout: bookingData.estimated_payout || 0,
          stay_completed: bookingData.stay_completed || false,
          note: bookingData.note || null,
          nights: nights,
          property_short_name: "",
          booking_id: "",
        })
        .select(BOOKING_SELECT_QUERY)
        .single();

      if (error) {
        console.error("Error creating booking:", error);
        throw error;
      }

      return {
        ...booking,
        status: booking.status as "Reservada" | "Cancelada",
        payment_status: booking.payment_status as 'pending' | 'partial' | 'paid',
      } as Booking;
    },
    onSuccess: () => {
      invalidateBookingQueries(queryClient);
      toast({
        title: "Reservación creada",
        description: "La reservación ha sido creada exitosamente",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Error al crear la reservación: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

/**
 * Hook to update an existing booking
 */
export const useUpdateBooking = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      bookingData,
    }: {
      id: string;
      bookingData: BookingFormData;
    }): Promise<Booking> => {
      // Validate origin_id
      if (!bookingData.origin_id || bookingData.origin_id.trim() === '') {
        throw new Error('El origen es requerido');
      }

      const guestId = await findOrCreateGuest(bookingData);

      const { data: booking, error } = await supabase
        .from("bookings")
        .update({
          property_id: bookingData.property_id,
          origin_id: bookingData.origin_id,
          check_in: bookingData.check_in,
          check_out: bookingData.check_out,
          guest_id: guestId,
          guest_name: bookingData.guest_name,
          guest_email: bookingData.guest_email || null,
          guest_phone: bookingData.guest_phone || null,
          total_guests: bookingData.total_guests,
          children: bookingData.children || 0,
          total_payment: bookingData.total_payment,
          cleaning_fee: bookingData.cleaning_fee || 0,
          base_payment: bookingData.base_payment || 0,
          pets_fee: bookingData.pets_fee || 0,
          early_checkin_fee: bookingData.early_checkin_fee || 0,
          late_checkout_fee: bookingData.late_checkout_fee || 0,
          other_fees: bookingData.other_fees || 0,
          estimated_payout: bookingData.estimated_payout || 0,
          stay_completed: bookingData.stay_completed || false,
          note: bookingData.note || null,
        })
        .eq("id", id)
        .select(BOOKING_SELECT_QUERY)
        .single();

      if (error) {
        console.error("Error updating booking:", error);
        throw error;
      }

      return {
        ...booking,
        status: booking.status as "Reservada" | "Cancelada",
        payment_status: booking.payment_status as 'pending' | 'partial' | 'paid',
      } as Booking;
    },
    onSuccess: () => {
      invalidateBookingQueries(queryClient);
      toast({
        title: "Reservación actualizada",
        description: "La reservación ha sido actualizada exitosamente",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Error al actualizar la reservación: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};
